import { Flex, Grid, Label, Loader } from "@aws-amplify/ui-react";
import { CCard } from "@coreui/react";
import { useEffect, useRef, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";
import moment from "moment";

import { globalStrings } from "config/translations/es";
import { getBranch, getCompany, getCostCenter } from "graphql/queries";
import { updateServiceOrder } from "graphql/mutations";
import { Edit } from "@mui/icons-material";

const ServiceOrderInfo = ({ serviceOrder }) => {
  const [company, setCompany] = useState({ data: {}, loading: false });
  const [branch, setBranch] = useState({ data: {}, loading: false });
  const [costCenter, setCostCenter] = useState({ data: {}, loading: false });
  const [createdAt, setCreatedAt] = useState(serviceOrder?.createdAt ?? "");
  const inputRef = useRef(null);

  async function updateLogDate(id, date) {
    try {
      await API.graphql(
        graphqlOperation(updateServiceOrder, {
          input: {
            id,
            createdAt: new Date(date),
          },
        })
      );
    } catch (err) {
      console.log("Error update log date", err);
    }
  }

  const onChangeDate = (id, date) => {
    updateLogDate(id, date);
    setCreatedAt(date);
  };

  const handleEditClick = () => {
    inputRef.current?.showPicker?.();
    if (!inputRef.current?.showPicker) inputRef.current?.click();
  };

  const handleChange = (e) => {
    const newDate = e.target.value;
    if (newDate) onChangeDate(serviceOrder.id, newDate);
  };

  const getCompanyName = async (id) => {
    try {
      setCompany({ data: {}, loading: true });

      const companyData = await API.graphql(
        graphqlOperation(getCompany, { id })
      );
      const companyDataItem = companyData.data.getCompany;

      setCompany((prev) => ({ ...prev, data: companyDataItem }));
    } catch (err) {
      toast.error(strings.errors.fetchCompany);
    } finally {
      setCompany((prev) => ({ ...prev, loading: false }));
    }
  };

  const getBranchName = async (id) => {
    try {
      setBranch({ data: {}, loading: true });

      const branchData = await API.graphql(graphqlOperation(getBranch, { id }));
      const branchDataItem = branchData.data.getBranch;

      setBranch((prev) => ({ ...prev, data: branchDataItem }));
    } catch (err) {
      toast.error(strings.errors.fetchBranch);
    } finally {
      setBranch((prev) => ({ ...prev, loading: false }));
    }
  };

  const getCostCenterName = async (id) => {
    try {
      setCostCenter({ data: {}, loading: true });

      const costCenterData = await API.graphql(
        graphqlOperation(getCostCenter, { id })
      );
      const costCenterDataItem = costCenterData.data.getCostCenter;

      setCostCenter((prev) => ({ ...prev, data: costCenterDataItem }));
    } catch (err) {
      toast.error(strings.errors.fetchCostCenter);
    } finally {
      setCostCenter((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    if (!serviceOrder.companyName && serviceOrder.companyID)
      getCompanyName(serviceOrder.companyID);
    if (!serviceOrder.branchName && serviceOrder.branchID)
      getBranchName(serviceOrder.branchID);
    if (!serviceOrder.costCenterName && serviceOrder.costCenterID)
      getCostCenterName(serviceOrder.costCenterID);
    if (serviceOrder.createdAt) setCreatedAt(serviceOrder.createdAt);
  }, [serviceOrder]);

  return (
    <CCard
      className="p-3"
      borderRadius="medium"
      variation="outlined"
      style={{ height: "100%" }}
    >
      <span className="fs-5 mb-2">{strings.serviceOrder}</span>
      <Grid gap="25px">
        <Grid
          gap="25px"
          templateColumns={{ base: "1fr 1fr", xl: "1fr 1fr 1fr 1fr" }}
        >
          <Grid>
            <Label>{strings.company}</Label>
            {company.loading ? (
              <Loader />
            ) : (
              <span className="text-muted">
                {serviceOrder.companyName || company.data.name}
              </span>
            )}
          </Grid>
          <Grid>
            <Label>{strings.branch}</Label>
            {branch.loading ? (
              <Loader />
            ) : (
              <span className="text-muted">
                {serviceOrder.branchName || branch.data.name}
              </span>
            )}
          </Grid>
          <Grid>
            <Label>{strings.code}</Label>
            <span className="text-muted">{serviceOrder.code}</span>
          </Grid>
          <Grid>
            <Label>{strings.name}</Label>
            <span className="text-muted">{serviceOrder.title}</span>
          </Grid>
          <Grid>
            <Label>{strings.billDate}</Label>
            <span className="text-muted">{serviceOrder.billDate}</span>
          </Grid>
          <Grid>
            <Label>{strings.billValue}</Label>
            <span className="text-muted">{serviceOrder.billValue}</span>
          </Grid>
          <Grid>
            <Label>{strings.billCurrency}</Label>
            <span className="text-muted">{serviceOrder.billCurrency}</span>
          </Grid>
          <Grid>
            <Label>{strings.status}</Label>
            <span className="text-muted">
              {globalStrings.serviceOrderStatus[serviceOrder.status]}
            </span>
          </Grid>
          <Grid>
            <Label>{strings.costCenter}</Label>
            {costCenter.loading ? (
              <Loader />
            ) : (
              <span className="text-muted">
                {serviceOrder.costCenterName || costCenter.data.name}
              </span>
            )}
          </Grid>
          <Grid>
            <Label>{strings.geolocation}</Label>
            <span className="text-muted">
              {!!serviceOrder.geolocation
                ? strings.activated
                : strings.desactivated}
            </span>
          </Grid>
          <Grid>
            <Label>{strings.createdAt}</Label>
            <Flex gap="5px" alignItems="center">
              <span className="text-muted">
                {moment(createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </span>
              <Edit
                color="action"
                fontSize="14"
                style={{ cursor: "pointer" }}
                onClick={handleEditClick}
              />
              <input
                ref={inputRef}
                type="datetime-local"
                style={{
                  opacity: 0,
                  position: "absolute",
                  pointerEvents: "none",
                }}
                defaultValue={moment(createdAt).format("YYYY-MM-DDTHH:mm")}
                onChange={handleChange}
              />
            </Flex>
          </Grid>
        </Grid>
        <Grid gap="25px">
          <Grid>
            <Label>{strings.description}</Label>
            <span className="text-muted">{serviceOrder.description}</span>
          </Grid>
        </Grid>
      </Grid>
    </CCard>
  );
};

const strings = new LocalizedStrings({
  es: {
    serviceOrder: "Orden de Servicio",
    code: "Código",
    name: "Nombre",
    description: "Descripción",
    billDate: "Fecha de facturación",
    billValue: "Valor de la factura",
    billCurrency: "Moneda de la factura",
    company: "Empresa",
    branch: "Sede",
    costCenter: "Centro de Costo",
    start: "Fecha Inicio de Ejecución",
    end: "Fecha Final de Ejecución",
    color: "Color",
    observations: "Observaciones",
    status: "Estado",
    geolocation: "Geolocalización para la ejecución",
    createdAt: "Fecha de creación",
    activated: "Activado",
    desactivated: "Desactivado",
    errors: {
      fetchCompany: "Error al consultar la empresa",
      fetchBranch: "Error al consultar la sede",
      fetchCostCenter: "Error al consultar el centro de costo",
      fetchEquipment: "Error al consultar el equipo",
    },
  },
});

export default ServiceOrderInfo;
